/**
 * Touch-mode detection — devices where the desktop pointer-lock flow
 * can't work and the on-screen TouchControls take over.
 *
 * The trigger is the REAL capability gap, not a UA sniff:
 *   - iOS/iPadOS Safari never exposes the Pointer Lock API (not even with
 *     a mouse or the Magic Keyboard trackpad paired), so
 *     `requestPointerLock` is simply absent → touch mode.
 *   - A coarse primary pointer + a touchscreen (phones, Android tablets —
 *     where pointer lock may exist but is useless without a mouse) →
 *     touch mode.
 *
 * A Windows touchscreen laptop keeps mouse mode: its primary pointer is
 * fine and pointer lock is available. `?touch=1` / `?touch=0` force
 * either path for testing from any device.
 *
 * Also stamps the `touch-mode` body class (once) so CSS can gate
 * touch-only rules — TouchControls styles hang off it.
 */
let cached: boolean | null = null;

export function isTouchMode(): boolean {
  if (cached !== null) return cached;
  const forced = new URLSearchParams(window.location.search).get("touch");
  if (forced === "1") {
    cached = true;
    console.log("[fps] touch mode ON (?touch=1)");
  } else if (forced === "0") {
    cached = false;
    console.log("[fps] touch mode OFF (?touch=0)");
  } else {
    const noPointerLock =
      typeof Element.prototype.requestPointerLock !== "function";
    const coarsePrimary =
      navigator.maxTouchPoints > 0 &&
      window.matchMedia("(pointer: coarse)").matches;
    // iPadOS Safari masquerades as a Mac by default ("Macintosh" UA,
    // desktop-site mode) and reports a FINE primary pointer while a
    // trackpad/mouse is paired — but still never gets pointer lock. A
    // real Mac always has maxTouchPoints 0, so touch points on a "Mac"
    // can only be an iPad. Belt-and-suspenders alongside the capability
    // checks in case a future WebKit stubs requestPointerLock in.
    const applePad =
      navigator.maxTouchPoints > 1 &&
      /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    cached = noPointerLock || coarsePrimary || applePad;
    console.log(
      `[fps] touch mode ${cached ? "ON" : "OFF"} ` +
        `(pointerLock=${!noPointerLock} coarse=${coarsePrimary} applePad=${applePad} ` +
        `maxTouchPoints=${navigator.maxTouchPoints})`,
    );
  }
  if (cached) document.body.classList.add("touch-mode");
  return cached;
}
